/**
 * @file orderStatusIcons.js
 * @version 1.4.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-18
 * @modified 2026-10-22
 *
 * 1.2.0 (2026-10-14, Cam): the billed icon reads the row's `billed_status`
 * (and `haulage_billed_status`) — the same SQL as the Status filter's Billed
 * sections (server utils/orderStatusFilters) — and the invoiced icon shows a
 * draft invoice (`draft_invoice_no`) apart: a draft is not invoiced.
 *
 * The invoiced and billed status icons, and the colours behind them.
 *
 * Lifted out of SearchCustomerOrders so anything else showing "is this order
 * invoiced / billed" shows the same icon in the same colour. The pipeline
 * icons (cart, mail, confirm, truck, package) followed when Price Orders took
 * on the same Status column, so the two screens cannot colour an order
 * differently.
 *
 * Two shapes, because two callers know different things. The Sales Orders row
 * knows the full status columns and the PO counts; a single order or PO line
 * knows only whether an invoice or a bill exists against it.
 *
 * 1.3.0 (2026-10-15, Cam): an order refused on delivery and waiting to go again
 * (`redelivery`, utils/redelivery) — the package icon reads "Held at <store>"
 * / "Redelivery DD/MM/YY" in amber, and the receipt icon is not red (it is
 * not late being invoiced: it has not been delivered).
 *
 * 1.4.0 (2026-10-22, Cam): Despatched — a third-party order from our stock
 * collected by the haulier, before its delivery date — reads "Despatched —
 * collected DD/MM/YYYY" on the package icon.
 */

import { redeliveryDeliveryStatus, redeliveryPillText } from './redelivery';
import { collectedText } from './suppliedQty';

export const STATUS_COLORS = {
  muted: 'rgba(var(--text-muted-rgb), 0.5)',
  primary: 'var(--text-primary)',
  error: 'var(--color-error)',
  warning: 'var(--color-warning)',
  success: 'var(--color-success)'
};

export const INVOICED_ICON = 'receipt';
export const BILLED_ICON = 'receipt_long';

/**
 * Sales Orders row → the receipt icon.
 *
 * Muted until delivered, because an undelivered order is not late being
 * invoiced; red once delivered and still not invoiced, because then it is.
 */
export function invoicedIconForOrder(order) {
  const delivery = order?.delivery_status || 'Not Despatched';
  const invoiced = order?.invoiced_status || 'Not Invoiced';
  // A draft (a delivery draft awaiting pricing) is not invoiced, but it is
  // not forgotten either: amber, named, wherever the order is.
  if (invoiced !== 'Invoiced' && order?.draft_invoice_no) {
    return { color: STATUS_COLORS.warning, title: `Draft Invoice ${order.draft_invoice_no} — awaiting pricing / approval in Pricing & Costing` };
  }
  // Refused and waiting to go again: not delivered yet (utils/redelivery).
  if (invoiced !== 'Invoiced' && redeliveryDeliveryStatus(order?.redelivery)) {
    return { color: STATUS_COLORS.muted, title: `Not Invoiced — ${redeliveryPillText(order.redelivery)}` };
  }
  if (delivery !== 'Delivered') return { color: STATUS_COLORS.muted, title: 'Not Invoiced' };
  if (invoiced === 'Not Invoiced') return { color: STATUS_COLORS.error, title: 'Not Invoiced' };
  return { color: STATUS_COLORS.success, title: 'Invoiced' };
}

/**
 * Sales Orders row → the billed icon. From the row's billed_status where the
 * server sent it (the Status filter's Billed section), else the PO counts.
 * A third-party order's PO-H is named after it.
 */
export function billedIconForOrder(order) {
  const state = order?.billed_status;
  if (state) {
    const poCount = parseInt(order?.po_count, 10) || 0;
    const billedCount = parseInt(order?.po_billed_count, 10) || 0;
    const map = {
      Billed: { color: STATUS_COLORS.success, title: 'POs Billed' },
      'Partially Billed': {
        color: STATUS_COLORS.warning,
        title: poCount ? `${billedCount} of ${poCount} POs Billed` : 'POs Partially Billed'
      },
      'Not Billed': { color: STATUS_COLORS.error, title: 'Not Billed' },
      'No POs': { color: STATUS_COLORS.muted, title: 'No POs — nothing to bill' }
    };
    const icon = map[state] || { color: STATUS_COLORS.muted, title: state };
    const haulage = order?.haulage_billed_status;
    return haulage
      ? { ...icon, title: `${icon.title} · PO-H ${haulage === 'Billed' ? 'billed' : haulage === 'Not Billed' ? 'not billed' : 'partially billed'}` }
      : icon;
  }
  return billedIconFromCounts(order);
}

function billedIconFromCounts(order) {
  const poCount = parseInt(order?.po_count, 10) || 0;
  const billedCount = parseInt(order?.po_billed_count, 10) || 0;
  const po = order?.po_status || 'No POs Linked';
  if (po === 'No POs Linked') return { color: STATUS_COLORS.muted, title: 'Not Billed' };
  if (poCount > 0 && billedCount === 0) return { color: STATUS_COLORS.error, title: 'Not Billed' };
  if (billedCount >= poCount) return { color: STATUS_COLORS.success, title: 'POs Billed' };
  if (billedCount > 0) return { color: STATUS_COLORS.warning, title: `${billedCount} of ${poCount} POs Billed` };
  return { color: STATUS_COLORS.muted, title: 'Not Billed' };
}

/**
 * A line that knows only `invoiced` / `billed`.
 *
 * Deliberately muted rather than red when there is no invoice or bill. On the
 * Sales Orders screen red means "this should have happened by now"; where these
 * are used — deciding whether it is safe to reprice a line — not being invoiced
 * is the good case, and colouring ten healthy rows red would say the opposite
 * of what is meant.
 */
export function invoicedIcon({ invoiced } = {}) {
  return invoiced
    ? { color: STATUS_COLORS.success, title: 'Invoiced — repricing will disagree with the invoice' }
    : { color: STATUS_COLORS.muted, title: 'Not invoiced' };
}

export function billedIcon({ billed } = {}) {
  return billed
    ? { color: STATUS_COLORS.success, title: 'Billed — repricing will disagree with the bill' }
    : { color: STATUS_COLORS.muted, title: 'Not billed' };
}

/**
 * Sales Orders row → the pipeline icons: cart (POs linked), mail (sent),
 * confirm, truck (transport) and package (delivery), plus the invoiced
 * receipt. Each is { color, title }.
 */
export function pipelineIconsForOrder(order) {
  const { muted, primary, error, warning, success } = STATUS_COLORS;
  const po = order.po_status || 'No POs Linked';
  const sent = order.sent_status || 'No POs Sent';
  const confirmed = order.confirmed_status || 'No POs Confirmed';
  const transport = order.transport_status || 'Not Planned';
  const delivery = order.delivery_status || 'Not Despatched';

  /*
   * Third-party haulage. The server counts the PO-H alongside any PO-D for
   * Sent, Confirmed and Delivery (so one sent and the other not reads as
   * partial), and its Transport status IS the haulage: no PO-H, PO-H not
   * sent, PO-H sent. PO Linked stays about the product's PO-Ds only.
   */
  const thirdParty = order.excel_agri_delivery_terms === 'Third-Party Delivery';
  const hasHaulagePo = Array.isArray(order.haulage_pos) && order.haulage_pos.length > 0;

  // Cart — PO Linked. An all-stock third-party order has nothing to buy in.
  const cartMap = { 'No POs Linked': { color: error, title: 'No POs Linked' }, 'POs Partially Linked': { color: warning, title: 'POs Partially Linked' }, 'POs Linked': { color: success, title: 'POs Linked' } };
  const cart = (thirdParty && po === 'No POs Linked' && order.needs_supply_po === false)
    ? { color: muted, title: 'No PO needed — from stock' }
    : (cartMap[po] || cartMap['No POs Linked']);

  // Mail — PO Sent (muted if no POs linked — counting a PO-H)
  let mail;
  if (po === 'No POs Linked' && !hasHaulagePo) { mail = { color: muted, title: 'Not Sent' }; }
  else if (sent === 'No POs Sent') { mail = { color: error, title: 'No POs Sent' }; }
  else if (sent === 'POs Partially Sent') { mail = { color: warning, title: 'POs Partially Sent' }; }
  else { mail = { color: success, title: 'POs Sent' }; }

  // Check Circle — PO Confirmed (muted if no POs sent)
  let confirm;
  if (sent === 'No POs Sent') { confirm = { color: muted, title: 'Not Confirmed' }; }
  else if (confirmed === 'No POs Confirmed') { confirm = { color: error, title: 'No POs Confirmed' }; }
  else if (confirmed === 'POs Partially Confirmed') { confirm = { color: warning, title: 'POs Partially Confirmed' }; }
  else { confirm = { color: success, title: 'POs Confirmed' }; }

  // Truck — Transport (muted only when no POs linked; error when POs exist but transport not planned)
  let truck;
  if (thirdParty) {
    if (transport === 'Transport Planned') truck = { color: success, title: 'Third-party haulage arranged (PO-H sent)' };
    else if (transport === 'Partially Planned') truck = { color: warning, title: 'Third-party haulage: PO-H raised, not sent' };
    else truck = { color: error, title: 'Third-party haulage: no PO-H raised' };
  }
  else if (po === 'No POs Linked') { truck = { color: muted, title: 'Not Planned' }; }
  else if (transport === 'Not Planned') { truck = { color: error, title: 'Not Planned' }; }
  else if (transport === 'Partially Planned') { truck = { color: warning, title: 'Partially Planned' }; }
  else { truck = { color: success, title: 'Transport Planned' }; }

  // Package — Delivery
  const pkgMap = {
    'Not Despatched': { color: muted, title: 'Not Despatched' },
    'Despatched': { color: primary, title: order.collected_date ? `Despatched — ${collectedText(order.collected_date).toLowerCase()}` : 'Despatched' },
    'Partially Despatched': { color: warning, title: 'Partially Despatched' },
    'In-Transit': { color: primary, title: 'In-Transit' },
    'Partially Delivered': { color: warning, title: 'Partially Delivered' },
    'Delivered': { color: success, title: 'Delivered' }
  };
  // Refused on delivery and waiting to go again (utils/redelivery).
  const pkg = redeliveryDeliveryStatus(order.redelivery)
    ? { color: warning, title: redeliveryPillText(order.redelivery) }
    : (pkgMap[delivery] || pkgMap['Not Despatched']);

  return { cart, mail, confirm, truck, pkg, receipt: invoicedIconForOrder(order) };
}

/** Price Orders row → whether the rep has been asked for prices. */
export function priceRequestIconForOrder(order) {
  const map = {
    'None Requested': { color: STATUS_COLORS.muted, title: 'No Price Requests Sent' },
    'Partially Requested': { color: STATUS_COLORS.warning, title: 'Some Price Requests Sent' },
    'All Requested': { color: 'var(--color-info)', title: 'All Price Requests Sent' }
  };
  return map[order?.price_request_status] || map['None Requested'];
}

/** The priced icon, from 'Priced' / 'Partially Priced' / 'Not Priced'. */
export function pricedIcon(status) {
  const color = status === 'Priced' ? STATUS_COLORS.success
    : status === 'Partially Priced' ? STATUS_COLORS.warning : STATUS_COLORS.error;
  return { color, title: status || 'Not Priced' };
}
